// ─────────────────────────────────────────────────────────────
// instrument.ts
//
// Sentry initialisation. Imported FIRST in src/main.tsx, before React
// and the store, so Sentry is running before the app boots. All config
// values live in sentry.config.ts; this file only wires init.
// ─────────────────────────────────────────────────────────────

import { useEffect } from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import {
  SENTRY_ENVIRONMENT,
  TRACES_SAMPLE_RATE,
  REPLAYS_SESSION_SAMPLE_RATE,
  REPLAYS_ON_ERROR_SAMPLE_RATE,
  TRACE_PROPAGATION_TARGETS,
  IGNORE_ERRORS,
  DENY_URLS,
  replayPrivacy,
} from "./sentry.config";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: SENTRY_ENVIRONMENT,
  // `release` is injected automatically by @sentry/vite-plugin at build time.
  integrations: [
    Sentry.reactRouterBrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(replayPrivacy),
    Sentry.captureConsoleIntegration({ levels: ["error"] }),
  ],
  // Tracing (frontend-only — see TRACE_PROPAGATION_TARGETS).
  tracesSampleRate: TRACES_SAMPLE_RATE,
  tracePropagationTargets: TRACE_PROPAGATION_TARGETS,
  // Session Replay (errors-only; masking is env-gated via replayPrivacy).
  replaysSessionSampleRate: REPLAYS_SESSION_SAMPLE_RATE,
  replaysOnErrorSampleRate: REPLAYS_ON_ERROR_SAMPLE_RATE,
  // Never attach cookies / IP / request bodies automatically.
  sendDefaultPii: false,
  ignoreErrors: IGNORE_ERRORS,
  denyUrls: DENY_URLS,
});
